<script lang="ts">
  /**
   * A value with a minus and a plus on either side: a delay in milliseconds,
   * a count of rounds. Sits at the right end of a `Row` (`trailing`). The
   * value is the game's own formatted string; the shell only draws it.
   *
   * Both buttons are named by the game ("Shorter delay", "Longer delay"), and
   * the group by `label`. At an end of the range the game passes
   * `canDecrease` / `canIncrease` false and that button is disabled.
   *
   * Test ids: `testids.decrease`, `testids.increase` and `testids.value`. No
   * defaults: a page usually has several.
   */
  interface Props {
    readonly label: string;
    readonly value: string;
    readonly ondecrease: () => void;
    readonly onincrease: () => void;
    readonly decreaseLabel: string;
    readonly increaseLabel: string;
    readonly canDecrease?: boolean;
    readonly canIncrease?: boolean;
    readonly testids?:
      | { readonly decrease?: string; readonly increase?: string; readonly value?: string }
      | undefined;
  }
  const {
    label,
    value,
    ondecrease,
    onincrease,
    decreaseLabel,
    increaseLabel,
    canDecrease = true,
    canIncrease = true,
    testids,
  }: Props = $props();
</script>

<span class="ui-stepper" role="group" aria-label={label}>
  <button
    type="button"
    class="ui-stepper-button"
    aria-label={decreaseLabel}
    disabled={!canDecrease}
    data-testid={testids?.decrease}
    onclick={ondecrease}
  >
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M6 12h12"
        fill="none"
        stroke="currentColor"
        stroke-width="2.6"
        stroke-linecap="round"
      />
    </svg>
  </button>
  <span class="ui-stepper-value" aria-live="polite" data-testid={testids?.value}>{value}</span>
  <button
    type="button"
    class="ui-stepper-button"
    aria-label={increaseLabel}
    disabled={!canIncrease}
    data-testid={testids?.increase}
    onclick={onincrease}
  >
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M12 6v12M6 12h12"
        fill="none"
        stroke="currentColor"
        stroke-width="2.6"
        stroke-linecap="round"
      />
    </svg>
  </button>
</span>

<style>
  .ui-stepper {
    display: inline-flex;
    align-items: center;
    gap: var(--ui-space-2);
    font-family: var(--ui-font);
  }

  .ui-stepper-button {
    width: 2.75rem;
    height: 2.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: var(--ui-radius-pill);
    background: var(--ui-surface-2);
    color: var(--ui-accent);
  }

  .ui-stepper-button:disabled {
    opacity: 0.45;
  }

  .ui-stepper-button:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
  }

  .ui-stepper-value {
    min-width: 3.6rem;
    text-align: center;
    font: var(--ui-text-body);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--ui-ink);
  }
</style>
